import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

const QUERY_KEY = 'cases';

export const useCases = (options = {}) => {
  const { 
    isPublished = true, 
    language = 'fr'
  } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { isPublished, language }],
    queryFn: async () => {
      let query = supabase
        .from('cases')
        .select(`
          *,
          translations:case_translations(title, excerpt, result, content, language_code),
          technologies:case_technologies(
            technology:technologies(id, name, logo_url)
          )
        `)
        .order('created_at', { ascending: false });

      if (isPublished !== null) {
        query = query.eq('is_published', isPublished);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      return data.map(item => ({
        ...item,
        title: item.translations?.find(t => t.language_code === language)?.title || item.translations?.[0]?.title,
        excerpt: item.translations?.find(t => t.language_code === language)?.excerpt || item.translations?.[0]?.excerpt,
        result: item.translations?.find(t => t.language_code === language)?.result || item.translations?.[0]?.result,
        content: item.translations?.find(t => t.language_code === language)?.content || item.translations?.[0]?.content,
      }));
    },
  });
};

export const useCase = (slug, language = 'fr') => {
  return useQuery({
    queryKey: [QUERY_KEY, slug, language],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cases')
        .select(`
          *,
          translations:case_translations(title, excerpt, result, content, language_code),
          technologies:case_technologies(
            technology:technologies(id, name, logo_url)
          )
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      
      return {
        ...data,
        title: data.translations?.find(t => t.language_code === language)?.title || data.translations?.[0]?.title,
        excerpt: data.translations?.find(t => t.language_code === language)?.excerpt || data.translations?.[0]?.excerpt,
        result: data.translations?.find(t => t.language_code === language)?.result || data.translations?.[0]?.result,
        content: data.translations?.find(t => t.language_code === language)?.content || data.translations?.[0]?.content,
      };
    },
    enabled: !!slug,
  });
};

export const useCreateCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (caseData) => {
      const { data, error } = await supabase.from('cases').insert(caseData).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useUpdateCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase.from('cases').update({ ...updates, updated_at: new Date().toISOString() }).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};

export const useDeleteCase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('cases').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });
};
